/* eslint-disable max-lines */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'
import moment from 'moment'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'

import AddButton from 'components/AddButton'
import Checkbox from 'components/Checkbox'
import Table from 'components/Table'

import SiteMessage from 'pages/shared/SiteMessage'

import { shortDateTime } from 'helpers/formatters'

import useScreenSize from 'hooks/useScreenSize'
import useSiteMessages from 'hooks/useSiteMessages'
import { useStoreContext } from 'hooks/StoreContext'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize, LoadingSinglePanePage } from 'styles/shared'

const SiteMessages = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now())

  useSiteMessages({ listForAllBrands: true, getPublishedOnly: false, refreshTimestamp })

  const navigate = useNavigate()
  const screenSize = useScreenSize()

  const { siteMessages, areSiteMessagesLoaded } = useStoreContext()

  const [showArchived, setShowArchived] = useState(false)

  if (!areSiteMessagesLoaded) {
    return <LoadingSinglePanePage />
  }

  const sortedSiteMessages = () => (
    _.chain(siteMessages)
      .filter((siteMessage) => !siteMessage.archivedAt || showArchived)
      .sortBy(siteMessage => {
        let sortValue = moment(siteMessage.publishedAt || siteMessage.updatedAt).toDate().getTime()

        if (siteMessage.publishedAt) { sortValue = sortValue * 10 }
        if (siteMessage.siteBannerEnabled) { sortValue = sortValue * 100 }
        if (!siteMessage.archivedAt) { sortValue = sortValue * 100 }

        return sortValue
      })
      .reverse()
      .value()
  )

  const onClickArchive = (siteMessageId) => {
    Api.SiteMessages.archive(siteMessageId).then(() => { setRefreshTimestamp(Date.now()) })
  }

  const onClickRestore = (siteMessageId) => {
    Api.SiteMessages.restore(siteMessageId).then(() => { setRefreshTimestamp(Date.now()) })
  }

  return (
    <>
      <S.Header>
        <S.Checkbox
          label="Show archived messages"
          checked={showArchived}
          onChange={() => setShowArchived(!showArchived)}
        />
        <AddButton uri="/admin/site_messages/new" testId="Add Message">Add Message</AddButton>
      </S.Header>
      <Table
        items={siteMessages}
        noDataMessage={{
          title: 'No Site Messages',
          description: 'Click "Add Message" to add your first site message.'
        }}
      >
        <Table.Header>
          <S.MessageHeaderCell title="Message" />
          <S.StatusHeaderCell title="Site Banner" />
          <S.StatusHeaderCell title="Updates" />
        </Table.Header>

        {sortedSiteMessages().map((siteMessage) => (
          <S.Row key={siteMessage.id}>
            <S.MessageCell>
              <S.MessageContainer>
                <S.Message>
                  <S.SiteMessage messageType={siteMessage.messageType}>
                    {siteMessage.publishedAt && <SiteMessage.PublishedDate publishedAt={siteMessage.publishedAt} />}
                    <SiteMessage.Body
                      showCollapsed
                      siteMessage={siteMessage}
                    />
                  </S.SiteMessage>
                  <S.Footer>
                    <S.Roles>{siteMessage.roles.sort().join(', ')}</S.Roles>
                    <S.Brands>{siteMessage.brands.join(', ')}</S.Brands>
                  </S.Footer>
                </S.Message>
                <div>

                  <S.EditIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/site_messages/${siteMessage.id}/edit`)}
                    aria-label={`Edit Site Message with title: ${siteMessage.title}`}
                  >
                    Edit
                  </S.EditIcon>
                  { !siteMessage.archivedAt
                    ? <S.ArchiveOutlinedIcon
                        role="img"
                        aria-label="Archive Message"
                        onClick={() => onClickArchive(siteMessage.id)}
                      />
                    : <S.RestoreOutlinedIcon role="img" aria-label="Restore Message" onClick={() => onClickRestore(siteMessage.id)} />
                  }
                </div>

              </S.MessageContainer>
            </S.MessageCell>

            <S.StatusCell>
              <div>
                {screenSize === 'small' ? <S.SmallScreenLabel>Site Banner:</S.SmallScreenLabel> : null}

                <S.Status enabled={siteMessage.siteBannerEnabled}>
                  {siteMessage.siteBannerEnabled ? 'enabled' : 'disabled'}
                </S.Status>

                {siteMessage.siteBannerEnabled ? <S.Detail><b>Last Updated: </b>{shortDateTime(siteMessage.updatedAt)}</S.Detail> : null}
              </div>
            </S.StatusCell>

            <S.StatusCell>
              <div>
                {screenSize === 'small' ? <S.SmallScreenLabel>Updates:</S.SmallScreenLabel> : null}

                <S.Status enabled={siteMessage.published}>
                  {siteMessage.published ? 'published' : 'not published'}
                </S.Status>
              </div>
            </S.StatusCell>
          </S.Row>
        ))}
      </Table>
    </>
  )
}

export default SiteMessages

const iconStyle = (iconType) => {
  return css`
    margin: 8px 0 0 0px;
    cursor: pointer;
    color: ${color(iconType)};
    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const MESSAGE_COLUMN_FLEX_VALUE = '4'
const STATUS_COLUMN_FLEX_VALUE = '1'

const S = {
  Header: styled.div`
    display: flex;
    justify-content: space-between;
    padding: 20px 0px;
  `,
  Checkbox: styled(Checkbox)`
    text-transform: uppercase;
    font-weight: 600;
    color: ${color('pageTitle')};
  `,
  MessageHeaderCell: styled(Table.HeaderCell)`
    flex: ${MESSAGE_COLUMN_FLEX_VALUE};
  `,
  StatusHeaderCell: styled(Table.HeaderCell)`
    flex: ${STATUS_COLUMN_FLEX_VALUE};
  `,
  Row: styled(Table.Row)`
    padding: 10px 0;

    &:last-child {
      border-bottom: none;
    }

    @media ${screenSize.small} {
      flex-flow: row wrap;
      padding: 10px 0 30px;
    }
  `,
  MessageCell: styled(Table.Cell)`
    flex: ${MESSAGE_COLUMN_FLEX_VALUE};
    flex-direction: column;
    margin-right: 15px;

    @media ${screenSize.small} {
      flex: 1 1 100%;
      margin-right: 0;
      margin-bottom: 15px;
    }
  `,
  SiteMessage: styled(SiteMessage)`
    cursor: pointer;
    border: 1px solid ${color('border')};
  `,
  Footer: styled.div`
    display: flex;
    flex-direction: row;
  `,
  Roles: styled.div`
    color: ${color('secondaryText')};
    font-style: italic;
    flex: 1;
  `,
  Brands: styled.div`
    color: ${color('secondaryText')};
    font-style: italic;
    flex: 1;
    text-align: right;
  `,
  StatusCell: styled(Table.Cell)`
    flex: ${STATUS_COLUMN_FLEX_VALUE};
    flex-direction: column;

    @media ${screenSize.small} {
      display: inline-block;
      width: 50%;
      padding-left: 5px;
    }
  `,
  Status: styled.div.withConfig({
    shouldForwardProp: (prop) => !['enabled'].includes(prop)
  })`
    display: inline-block;
    padding: 4px 10px;
    margin-bottom: 5px;
    text-align: center;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: uppercase;
    border: 1px solid ${color('border')};
    border-radius: 14px;

    ${({ enabled }) => {
    if (enabled) {
      return `
          border-color: ${color('siteMessageEnabledStatusBorder')};
          background-color: ${color('positiveStatus')};
          color: ${color('siteMessageStatusEnabledText')};
          `
    } else {
      return `
          color: ${color('siteMessageStatusDisabledText')};
        `
    }
  }}
  `,
  SmallScreenLabel: styled.div`
    font-weight: bold;
  `,
  Detail: styled.div`
    font-size: 13px;
  `,
  MessageContainer: styled.div`
    display: flex;
    gap: 16px;
  `,
  Message: styled.div`
    width: 100%;
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
  ${iconStyle('actionableIcon')}
`,
  RestoreOutlinedIcon: styled(RestoreOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `,
  EditIcon: styled(EditIcon)`
    color: ${color('EditIcon')};
  `
}
